import Phaser from "phaser";

import type { DiscordBridge } from "../discord/DiscordBridge.ts";
import { RoomClient } from "../net/RoomClient.ts";
import { COLORS, FONT } from "../ui/theme.ts";
import { GameScene } from "./GameScene.ts";

/**
 * First scene: performs the Discord handshake and room join while showing
 * progress, then hands the connected RoomClient to the game. Any failure
 * stays on screen with a retry instead of a blank iframe.
 */
export class BootScene extends Phaser.Scene {
  static readonly KEY = "boot";

  private _status!: Phaser.GameObjects.Text;
  private _retry!: Phaser.GameObjects.Text;
  private readonly _bridge: DiscordBridge;

  constructor(bridge: DiscordBridge) {
    super(BootScene.KEY);
    this._bridge = bridge;
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLORS.background);

    this.add
      .text(width / 2, height / 2 - 60, "Odds Are", {
        fontFamily: FONT.family,
        fontSize: `${FONT.title}px`,
        color: COLORS.accentText,
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    this._status = this.add
      .text(width / 2, height / 2, "Connecting to Discord", {
        fontFamily: FONT.family,
        fontSize: `${FONT.body}px`,
        color: COLORS.textMuted,
        align: "center",
        wordWrap: { width: width - 48 },
      })
      .setOrigin(0.5);

    this._retry = this.add
      .text(width / 2, height / 2 + 60, "Tap to retry", {
        fontFamily: FONT.family,
        fontSize: `${FONT.body}px`,
        color: COLORS.text,
        backgroundColor: "#3a2f5c",
        padding: { x: 16, y: 10 },
      })
      .setOrigin(0.5)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => void this._connect());

    void this._connect();
  }

  private async _connect(): Promise<void> {
    this._retry.setVisible(false);
    try {
      this._status.setText("Connecting to Discord");
      const session = await this._bridge.connect((status) => this._status.setText(status));
      this._status.setText(`Joining the table as ${session.displayName}`);
      const client = new RoomClient(session);
      await client.connect();
      this.scene.start(GameScene.KEY, { client });
    } catch (error) {
      const message = describeError(error);
      console.error("[boot] connection failed:", message, "code:", errorCode(error), error);
      this._status.setText(`Could not connect: ${message}`);
      this._retry.setVisible(true);
    }
  }
}

/** Discord SDK and Colyseus both throw plain objects at times, so dig for a message. */
function describeError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object" && error !== null) {
    const message = (error as Record<string, unknown>).message;
    if (typeof message === "string" && message) return message;
  }
  return "Something went wrong";
}

function errorCode(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const code = (error as Record<string, unknown>).code;
    if (typeof code === "number" || typeof code === "string") return String(code);
  }
  return "none";
}
